"use client"

import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Sparkles } from "lucide-react";

// Keyframes for the glistening text effect
const letterAnim = keyframes`
  0%, 100% {
    text-shadow: none;
    color: rgba(255, 255, 255, 0.6);
  }
  50% {
    text-shadow: 0 0 3px rgba(255, 255, 255, 0.5);
    color: #fff;
  }
`;

// Keyframes for the sparkle icon flicker
const flicker = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`;


const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  height: 2.25rem;
  padding: 0 1.25rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: white;
  background-color: ${props => props.$hasValue ? '#006de2' : '#93c5fd'};
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isLoading ? 1 : (props.disabled ? 0.5 : 1)};
  transition: background-color 0.2s, opacity 0.2s, box-shadow 0.3s;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background-color: ${props => props.$hasValue ? '#0056b3' : '#93c5fd'};
  }

  &:focus {
    outline: none;
  }

`;

const SparkleIcon = styled(Sparkles)`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  
  ${props => props.$isLoading && css`
    animation: ${flicker} 2s linear infinite;
    animation-delay: 0.5s;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.6));
  `}
`;

const TextWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  position: relative;
  min-width: ${props => props.$isLoading ? '5.5em' : 'auto'};
`;

const StaticText = styled.span`
  display: ${props => props.$isLoading ? 'none' : 'inline'};
`;

const AnimatedTextContainer = styled.span`
  display: ${props => props.$isLoading ? 'inline-flex' : 'none'};
  align-items: center;
`;

const AnimatedLetter = styled.span`
  display: inline-block;
  color: rgba(255, 255, 255, 0.6);
  animation: ${letterAnim} 1.2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

export function GenerateButton({ 
  onClick, 
  disabled, 
  isLoading,
  hasValue 
}) {
  const loadingText = "Generating";
  const normalText = "Generate";

  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      $isLoading={isLoading}
      $hasValue={hasValue}
    >
      <SparkleIcon $isLoading={isLoading} />
      <TextWrapper $isLoading={isLoading}>
        <StaticText $isLoading={isLoading}>{normalText}</StaticText>
        <AnimatedTextContainer $isLoading={isLoading}>
          {loadingText.split('').map((letter, index) => (
            <AnimatedLetter 
              key={index} 
              $delay={index * 0.08}
            >
              {letter}
            </AnimatedLetter>
          ))}
        </AnimatedTextContainer>
      </TextWrapper>
    </StyledButton>
  );
}

export default GenerateButton;
